
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Copy, Check, Mail, Share2, Link as LinkIcon, Users, X, Send } from "lucide-react";
import { showSuccessToast, showErrorToast } from "@/utils/toast-utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useTestInvitations } from "@/hooks/useTestInvitations";

interface ShareTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: string;
}

export const ShareTestDialog = ({ open, onOpenChange, testId }: ShareTestDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("link");

  const { invitations, isLoading: isLoadingInvitations, sendInvitations, isSending, deleteInvitation } = useTestInvitations(testId);

  useEffect(() => {
    if (open) {
      getShareLink();
    }
  }, [open]);

  const getShareLink = async () => {
    try {
      setIsLoading(true);
      console.log("Getting share link for test:", testId);
      
      const { data, error } = await supabase
        .from('tests')
        .select('share_token')
        .eq('id', testId)
        .single();

      if (error) {
        console.error("Error fetching test share token:", error);
        throw new Error(`Failed to get share link: ${error.message}`);
      }

      if (!data || !data.share_token) {
        console.error("No share token found for test:", testId);
        throw new Error("This test doesn't have a sharing token");
      }

      const baseUrl = window.location.origin;
      const link = `${baseUrl}/test/invite/${data.share_token}`;
      setInviteLink(link);
      console.log("Successfully retrieved share link");
    } catch (error: any) {
      console.error("Error getting share link:", error);
      showErrorToast({
        title: "Share Link Error",
        description: error.message || "Failed to get share link"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showSuccessToast({
        title: "Link Copied",
        description: "Link copied to clipboard"
      });
    } catch (error: any) {
      showErrorToast({
        title: "Copy Failed",
        description: "Failed to copy link"
      });
    }
  };

  const shareByEmail = () => {
    const subject = "Invitation to take an assessment";
    const body = `You have been invited to take an assessment. Click the following link to begin: ${inviteLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareByWhatsApp = () => {
    const text = `You have been invited to take an assessment. Click the following link to begin: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const handleAddEmail = () => {
    const input = emailInput.trim();
    
    if (!input) return;
    
    // Split by newlines, commas, semicolons, and spaces
    const emails = input
      .split(/[\n,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0);
    
    if (emails.length === 0) return;

    const validEmails: string[] = [];
    const invalidEmails: string[] = [];
    const duplicateEmails: string[] = [];

    emails.forEach(email => {
      if (!validateEmail(email)) {
        invalidEmails.push(email);
      } else if (emailList.includes(email) || validEmails.includes(email)) {
        duplicateEmails.push(email);
      } else {
        validEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      setEmailList([...emailList, ...validEmails]);
      setEmailInput("");
      showSuccessToast({
        title: "Emails Added",
        description: `${validEmails.length} email${validEmails.length > 1 ? 's' : ''} added successfully`
      });
    }

    if (invalidEmails.length > 0) {
      showErrorToast({
        title: "Invalid Email(s)",
        description: `${invalidEmails.length} invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`
      });
    }

    if (duplicateEmails.length > 0 && validEmails.length === 0 && invalidEmails.length === 0) {
      showErrorToast({
        title: "Duplicate Email(s)",
        description: "All emails have already been added"
      });
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailList(emailList.filter(e => e !== email));
  };

  const handleSendInvitations = async () => {
    if (emailList.length === 0) {
      showErrorToast({
        title: "No Emails",
        description: "Please add at least one email address"
      });
      return;
    }

    await sendInvitations.mutateAsync({ testId, emails: emailList });
    setEmailList([]);
    setActiveTab("invitations");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Test
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="link">
                <LinkIcon className="h-4 w-4 mr-2" />
                Share Link
              </TabsTrigger>
              <TabsTrigger value="invite">
                <Mail className="h-4 w-4 mr-2" />
                Add Candidates
              </TabsTrigger>
              <TabsTrigger value="invitations">
                <Users className="h-4 w-4 mr-2" />
                Invitations ({invitations?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="link" className="space-y-4 mt-4">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-foreground" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Share this link with your candidates. Each candidate will need to create or log into their account to take the test.
                    </p>
                    <div className="flex space-x-2">
                      <div className="relative flex-1">
                        <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          value={inviteLink}
                          readOnly
                          className="pl-9 flex-1 bg-background text-foreground border-border"
                        />
                      </div>
                      <Button
                        size="icon"
                        onClick={copyToClipboard}
                        variant="outline"
                        className="bg-background text-foreground hover:bg-accent border-border"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Share via:</p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={shareByEmail}
                        className="flex-1"
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Email
                      </Button>
                      <Button
                        variant="outline"
                        onClick={shareByWhatsApp}
                        className="flex-1"
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="invite" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add candidate email addresses to your invitation list. You can paste multiple emails separated by commas, semicolons, or new lines.
                  </p>
                  <div className="space-y-2">
                    <textarea
                      placeholder="Enter email addresses (separated by commas, semicolons, or new lines)"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleAddEmail();
                        }
                      }}
                      className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      rows={3}
                    />
                    <Button onClick={handleAddEmail} variant="outline" className="w-full">
                      Add Email{emailInput.includes(',') || emailInput.includes('\n') || emailInput.includes(';') ? 's' : ''}
                    </Button>
                  </div>
                </div>

                {emailList.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Email List ({emailList.length})</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                      {emailList.map((email) => (
                        <div key={email} className="flex items-center justify-between bg-muted rounded px-3 py-2">
                          <span className="text-sm">{email}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemoveEmail(email)}
                            className="h-6 w-6"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={handleSendInvitations}
                      disabled={isSending}
                      className="w-full"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Users className="mr-2 h-4 w-4" />
                          Add {emailList.length} Candidate{emailList.length > 1 ? "s" : ""} to List
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="invitations" className="space-y-4 mt-4">
              {isLoadingInvitations ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-foreground" />
                </div>
              ) : invitations && invitations.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  <p className="text-sm text-muted-foreground mb-3">
                    View all candidates added to the invitation list for this test.
                  </p>
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between border rounded-md p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{invitation.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Invited {new Date(invitation.invited_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          invitation.status === "sent" ? "default" :
                          invitation.status === "accepted" ? "outline" :
                          invitation.status === "failed" ? "destructive" :
                          "secondary"
                        }>
                          {invitation.status}
                        </Badge>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteInvitation.mutate(invitation.id)}
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No candidates added yet</p>
                  <p className="text-sm">Use the "Add Candidates" tab to add candidates to your list</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="pt-4">
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};
